import { CREATE_REVIEW_REQUEST_EVENT, trackEvent } from 'utils/trackingUtils'
import { splitByType } from './AddReviewers/utils'
import { ourUuid } from 'utils'
import { InformalFeedbackRequestsService } from 'api/services/InformalFeedbackRequestsService'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { ArticleFlat } from 'types'
import { reportError } from 'utils/error'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { useState } from 'react'
import { useQueryClient } from 'react-query'
import { Value } from 'components/ShareSheet/RecipientSelector/Types'
import { useAuth } from 'hooks/UseAuth'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useStore } from 'react-redux'
import { InformalFeedbackRequest } from 'api/clients/InformalFeedbackRequestsClient'
import { useMemberProfile } from 'hooks/useMemberProfile'
import { DEFAULT_DOC_TITLE } from 'utils/constants'
import useIsMounted from 'ismounted'
import { ExternalLinkReviewAsset, ReviewAsset, ReviewAssetType } from './types'
import { ReviewFrequency, Weekday } from './ReviewersSelection/constants'

// TODO: Delete and replace once reviews as sharing is released
export interface SubmitOptions {
  onSuccess?: (request: InformalFeedbackRequest, submitVariables: SubmitVariables) => void
  onError?: () => void
}

export interface SubmitVariables {
  asset: ReviewAsset
  assetType: ReviewAssetType
  addMembers: boolean
  recipients: Value[]
  approvers: Value[]
  viewers: Value[]
  title: string
  message: string
  htmlMessage?: string
  endDate: string
  useReviewLayer: boolean
  reviewTemplateId?: number
  interval?: string
  layerId?: string
  frequency?: ReviewFrequency
  weekdays: Weekday[]
  permission?: 'edit' | 'comment' | 'read'
}

export type SubmitHandler = (variables: SubmitVariables, options?: SubmitOptions) => void

interface Props {
  canBePrivate: boolean
  sectionUUID?: string
}

interface Options {
  onSuccess?: (request: InformalFeedbackRequest, submitVariables: SubmitVariables) => void
}

export const useSubmit = ({ canBePrivate, sectionUUID }: Props, { onSuccess }: Options = {}) => {
  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState(false)
  const auth = useAuth()
  const isMounted = useIsMounted()
  const currentWorkspace = useCurrentWorkspace()
  const { profile } = useMemberProfile(currentWorkspace?.id, auth?.id)
  const store = useStore()

  const submit: SubmitHandler = async (variables) => {
    if (isLoading) return

    const {
      asset,
      assetType,
      addMembers,
      recipients,
      approvers,
      viewers,
      title,
      message,
      htmlMessage,
      endDate,
      useReviewLayer,
      reviewTemplateId,
      layerId,
      permission = 'edit',
    } = variables

    setIsLoading(true)

    trackEvent(CREATE_REVIEW_REQUEST_EVENT, profile)

    const { users: reviewers, channels: channelReviewers, emails: emailReviewers } = splitByType(recipients, approvers)
    const { users: userViewers, channels: channelViewers, emails: emailViewers } = splitByType(viewers)

    let articleMainLayerId = undefined
    let requestLayerId = undefined
    let currentArticle = undefined
    let externalResourceInput = undefined

    if (assetType === ReviewAssetType.Article) {
      const article = asset as ArticleFlat
      articleMainLayerId = article?.main_layer ? article?.main_layer.id : article?.main_layer_id
      requestLayerId = layerId ? layerId : articleMainLayerId

      if (canBePrivate && useReviewLayer) {
        requestLayerId = ourUuid()
      }

      currentArticle = article
    }

    if (assetType === ReviewAssetType.ExternalLink) {
      const externalLinkAsset = asset as ExternalLinkReviewAsset
      externalResourceInput = {
        title: externalLinkAsset.title || DEFAULT_DOC_TITLE,
        external_link: externalLinkAsset.link || '',
        external_file_icon: externalLinkAsset.icon || '',
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        workspace_id: currentWorkspace!.id,
      }
    }

    const res = await InformalFeedbackRequestsService.create(
      store,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      currentWorkspace!.id,
      currentArticle?.id,
      {
        subject: title,
        message,
        htmlMessage,
        endsOn: endDate,
        reviewers,
        emailReviewers,
        channelReviewers,
        viewers: userViewers.map(({ id }) => id),
        channelViewers: channelViewers.map(({ id }) => id),
        emailViewers: emailViewers.map(({ email }) => email),
        permission,
        addMembers,
        layerId: requestLayerId,
        sectionUUID,
        reviewTemplateId,
      },
      externalResourceInput,
      queryClient,
    )

    if (res.type === SUCCESS_RESPONSE_TYPE) {
      onSuccess?.(res.data.informal_feedback_request, variables)
    } else {
      reportError(new Error('InformalFeedbackRequest received an error response'), {
        severity: 'error',
        metaData: { info: res.data },
      })
      showErrorMessageToast('Something went wrong, try again')
    }
    if (isMounted.current) setIsLoading(false)
  }

  return { submit, isLoading }
}
